import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth.ts';
import { ApiError } from '../api/client.ts';
import { useAuthStore } from '../state/authStore.ts';
import { useSeo } from '../hooks/useSeo.ts';
import { trackEvent } from '../analytics.ts';

type Mode = 'sign-in' | 'register';

/**
 * Combined sign-in + registration screen. Google sign-in lives at the top as a single CTA.
 * Below the "or" divider, a toggleable email + password form serves both flows.
 *
 * Account linking is server-side: if the user's email matches across the two flows we
 * collapse to one account. No special UI flag here — UserProvisioningService takes care of it.
 */
export default function SignInPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useAuthStore(s => s.setUser);
  const returnUrl = params.get('returnUrl') ?? '/';
  const externalError = params.get('error');

  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  useSeo({
    title: mode === 'register' ? 'Create your account' : 'Sign in',
    canonicalPath: '/sign-in',
  });

  const signIn = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.signInWithPassword(email, password),
    onSuccess: async () => {
      const me = await authApi.me();
      setUser(me);
      navigate(returnUrl.startsWith('/') ? returnUrl : '/');
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.errorCode === 'email_not_verified' || err.status === 403) {
          setNeedsVerification(true);
          setRegisteredEmail(email);
          setFormError(err.detail || 'Confirm your email first.');
        } else {
          setFormError(err.detail || err.title || 'Sign-in failed.');
        }
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    },
  });

  const register = useMutation({
    mutationFn: ({ email, password, displayName }: { email: string; password: string; displayName: string }) =>
      authApi.register(email, password, displayName),
    onSuccess: async (result) => {
      setFormError(null);
      // GA4 sign_up is a recommended event — fire it for both verification paths so the
      // funnel report has consistent numerator/denominator. The "method" param is GA4's
      // recommended dimension for splitting Google OAuth vs email signups in reports.
      trackEvent('sign_up', { method: 'password' });
      if (result.emailVerificationRequired) {
        // Verification mode (Auth:RequireEmailVerification=true) — show the check-inbox state.
        setNeedsVerification(true);
        setRegisteredEmail(result.email);
        return;
      }
      // No-verification mode — backend already issued the cookie. Refresh /auth/me so the
      // store has the user, then route to home.
      try {
        const me = await authApi.me();
        setUser(me);
      } catch { /* the cookie is set even if /me momentarily fails; navigate anyway */ }
      navigate(returnUrl.startsWith('/') ? returnUrl : '/');
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setFormError(err.detail || err.title || 'Registration failed.');
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    },
  });

  const resend = useMutation({
    mutationFn: (email: string) => authApi.resendVerification(email),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setNeedsVerification(false);
    if (!email || !password) return;
    if (mode === 'sign-in') {
      signIn.mutate({ email: email.trim(), password });
    } else {
      register.mutate({ email: email.trim(), password, displayName: displayName.trim() });
    }
  };

  if (needsVerification && registeredEmail) {
    return (
      <div className="sign-in">
        <h1>Check your email</h1>
        <p className="muted">
          We've sent a confirmation link to <strong>{registeredEmail}</strong>. Click it to
          finish setting up your account.
        </p>
        <p className="muted small">
          Didn't get the email?{' '}
          <button
            type="button"
            className="link-button"
            onClick={() => resend.mutate(registeredEmail)}
            disabled={resend.isPending}
          >
            {resend.isPending ? 'Sending…' : resend.isSuccess ? 'Sent — check again' : 'Resend it'}
          </button>
        </p>
        <p className="muted small">
          Wrong email?{' '}
          <button type="button" className="link-button" onClick={() => { setNeedsVerification(false); setMode('register'); }}>
            Start over
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="sign-in">
      <h1>{mode === 'register' ? 'Create your account' : 'Sign in to EAA Proof'}</h1>
      <p className="muted">
        {mode === 'register'
          ? 'Scan your site for EU Accessibility Act compliance and get a ready-to-publish statement.'
          : 'Welcome back. Sign in to run scans and access reports.'}
      </p>

      {externalError === 'google' && (
        <p className="error" role="alert">Google sign-in failed. Please try again.</p>
      )}

      <button
        type="button"
        className="cta cta-google"
        onClick={() => authApi.startGoogleSignIn(returnUrl)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{ marginRight: '0.5rem', verticalAlign: '-3px' }}>
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
        </svg>
        Continue with Google
      </button>

      <div className="auth-divider"><span>or</span></div>

      <form className="auth-form" onSubmit={onSubmit}>
        {mode === 'register' && (
          <label>
            <span>Name <span className="muted small">(optional)</span></span>
            <input
              type="text"
              autoComplete="name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              maxLength={120}
            />
          </label>
        )}

        <label>
          <span>Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            maxLength={256}
          />
        </label>

        <label>
          <span>Password</span>
          <input
            type="password"
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            required
            minLength={8}
            maxLength={128}
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          {mode === 'register' ? (
            <span className="muted small">At least 8 characters.</span>
          ) : (
            <Link to="/forgot-password" className="muted small auth-forgot">Forgot password?</Link>
          )}
        </label>

        {formError && <p className="error" role="alert">{formError}</p>}

        <button type="submit" className="cta" disabled={signIn.isPending || register.isPending}>
          {mode === 'register'
            ? (register.isPending ? 'Creating…' : 'Create account')
            : (signIn.isPending ? 'Signing in…' : 'Sign in')}
        </button>
      </form>

      <p className="muted small auth-toggle">
        {mode === 'sign-in' ? (
          <>
            New here?{' '}
            <button type="button" className="link-button" onClick={() => { setMode('register'); setFormError(null); }}>
              Create an account
            </button>
          </>
        ) : (
          <>
            Already have an account?{' '}
            <button type="button" className="link-button" onClick={() => { setMode('sign-in'); setFormError(null); }}>
              Sign in
            </button>
          </>
        )}
      </p>

      <p className="muted small">
        By continuing you agree to our <Link to="/terms">terms</Link> and{' '}
        <Link to="/privacy">privacy policy</Link>. We only use your email + name to
        provide the service.
      </p>
    </div>
  );
}
